import { useDispatch, useSelector } from "react-redux";
import { ApiStatus } from "../types/api/status";
import { CustomSnackbar } from "../components/common/CustomSnackbar/CustomSnackbar";
import { changeStatus, onChangeIsEdit, onFetchCategories, onFetchCourses, onFetchCourseWeb, onFetchTeachers, onSetEditCourseWeb, onSetOpenModal, onSetSelectedCategory, onSetSelectedCourse, onSetSelectedCourseWeb, onSetSelectedTeacher, selectCourseWebState } from "../redux/slices/courseWebSlice";
import { CourseWebAPI } from "../apis/CourseWebAPI";
import { Category, Course, CourseWeb, CourseWebCreateEdit, Teacher } from "../types/slices/coursesWebType";

export const useCourseWebStore = () => {
  const dispatch = useDispatch();
  const {
    coursesWeb,
    status,
    openModal,
    isEdit,
    editCourseWeb,
    selectedCourseWeb,
    selectedCourse,
    courses,
    categories,
    teachers,
    selectedCategory,
    selectedTeacher,
  } = useSelector(selectCourseWebState);

  const getCoursesWeb = async () => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.getCoursesWeb();
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      const { detail } = response.data;
      dispatch(onFetchCourseWeb(detail));
      dispatch(changeStatus(ApiStatus.FETCHED));
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const getCoursesWebSearch = async (term) => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.getCoursesWebSearch(term);
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      const { detail } = response.data;
      dispatch(onFetchCourseWeb(detail));
      dispatch(changeStatus(ApiStatus.FETCHED));
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const createCourseWebStore = async (data: any) => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.createCourseWeb(data);
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      dispatch(changeStatus(ApiStatus.FETCHED));
      CustomSnackbar("success", response.data.message || "");
      return true;
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const editCourseWebStore = async (id: number, data: any) => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.editCourseWeb(id, data);
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      dispatch(changeStatus(ApiStatus.FETCHED));
      CustomSnackbar("success", response.data.message || "");
      return true;
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const deleteCourseWebStore = async (id: number) => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.deleteCourseWeb(id);
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        CustomSnackbar("error", response.data.message || "");
        return false;
      }
      dispatch(changeStatus(ApiStatus.FETCHED));
      CustomSnackbar("success", response.data.message || "");
      return true;
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const setEditCourseWeb = (course: CourseWebCreateEdit) => {
    try {
      dispatch(onSetEditCourseWeb(course));
    } catch (error) {
      console.log(error);
    }
  };

  const setSelectedCourseWeb = (course: CourseWeb) => {
    try {
      dispatch(onSetSelectedCourseWeb(course));
    } catch (error) {
      console.log(error);
    }
  };

  const getCourse = async () => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.getCourse();
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      const { detail } = response.data;
      dispatch(onFetchCourses(detail));
      dispatch(changeStatus(ApiStatus.FETCHED));
    }
    catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  }

  const setSelectedCourse = (course: Course) => {
    try {
      dispatch(onSetSelectedCourse(course));
    } catch (error) {
      console.log(error);
    }
  }

  const getCategory = async () => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.getCategory();
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      const { detail } = response.data;
      dispatch(onFetchCategories(detail));
      dispatch(changeStatus(ApiStatus.FETCHED));
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const setSelectedCategory = (category: Category) => {
    try {
      dispatch(onSetSelectedCategory(category));
    } catch (error) {
      console.log(error);
    }
  };

  const getTeacher = async () => {
    try {
      dispatch(changeStatus(ApiStatus.FETCHING));
      const response = await CourseWebAPI.getTeacher();
      if (!response?.status) {
        dispatch(changeStatus(ApiStatus.FETCHED));
        return CustomSnackbar("error", response.data.message || "");
      }
      const { detail } = response.data;
      dispatch(onFetchTeachers(detail));
      dispatch(changeStatus(ApiStatus.FETCHED));
    } catch (error) {
      console.log(error);
      return dispatch(changeStatus(ApiStatus.FETCHED));
    }
  };

  const setSelectedTeacher = (teacher: Teacher) => {
    try {
      dispatch(onSetSelectedTeacher(teacher));
    } catch (error) {
      console.log(error);
    }
  };

  const setOpenModal = (status: boolean) => {
    try {
      dispatch(onSetOpenModal(status));
    } catch (error) {
      console.log(error);
    }
  };

  const changeIsEdit = (status: boolean) => {
    try {
      dispatch(onChangeIsEdit(status));
    } catch (error) {
      console.log(error);
    }
  };

  return {
    // states
    coursesWeb,
    status,
    editCourseWeb,
    selectedCourseWeb,
    selectedCourse,
    courses,
    categories,
    teachers,
    selectedCategory,
    selectedTeacher,
    openModal,
    isEdit,
    // actions
    getCoursesWeb,
    getCoursesWebSearch,
    createCourseWebStore,
    editCourseWebStore,
    setEditCourseWeb,
    deleteCourseWebStore,
    setSelectedCourseWeb,
    getCourse,
    setSelectedCourse,
    getCategory,
    setSelectedCategory,
    getTeacher,
    setSelectedTeacher,
    setOpenModal,
    changeIsEdit,
  };

};